/*
 * sa-facets.js
 *
 * Ported from orion-v3.0's assets/sa-facets.js. Provides:
 *   - jQuery-based popup open/close + accordion behaviour
 *   - <facet-filters-form> / <price-range> / <facet-remove>
 *     custom elements with the enhanced tab-aware behaviour the
 *     SA Collection Filters tabs system depends on (combined search
 *     params across the tab form + the sort form, dual-target re-
 *     render of #product-grid-container AND .collection-filter-tabs).
 *
 * H3.0 adaptations:
 *   - menu-drawer.bindEvents() call guarded with optional chaining —
 *     H3.0 has no <menu-drawer> element on the collection template.
 *   - customElements.define wrapped in `if (!customElements.get(...))`
 *     so re-registration during HMR / live reload doesn't throw.
 *   - Extra null guards around ProductCount / collection elements
 *     so initial render on an empty collection doesn't crash.
 *
 * Depends on jQuery being available globally (it is in H3.0 via the
 * existing theme bundle).
 */

function debounceFacets(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* Read --global-transition-duration off :root and convert to ms so
 * jQuery .slideUp() / .slideDown() can use the same duration the
 * rest of the theme transitions on (Theme Settings → Animation
 * Transition). Falls back to 300ms if the var is missing or
 * unreadable. */
function getSlideDuration() {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--global-transition-duration').trim();
    if (raw.endsWith('ms')) return parseFloat(raw);
    if (raw.endsWith('s')) return parseFloat(raw) * 1000;
  } catch (e) {}
  return 300;
}

document.addEventListener('DOMContentLoaded', function () {
  const slideDuration = getSlideDuration();

  /*
   * Scroll fades — mirrors the PDP Read More modal pattern. When
   * the filter list overflows its container, gradient overlays
   * pinned to the container's top and bottom fade in to signal
   * "more content this way". Each fade shows only while there's
   * still content to scroll towards on its side; both fade out
   * once the user reaches the corresponding edge.
   *
   * Update triggers:
   *   - scroll on the container
   *   - viewport resize (max-height depends on 100vh)
   *   - popup open (clientHeight reads 0 while the panel is hidden,
   *     so a re-check after the open transition catches the real
   *     dimensions)
   */
  const SCROLL_CONTAINER = '[data-filters-scroll-container]';
  const FADE_TOP = '[data-filters-scroll-fade-top]';
  const FADE_BOTTOM = '[data-filters-scroll-fade-bottom]';
  const VISIBLE_CLASS = 'is-visible';

  function updateFilterScrollFades() {
    const container = document.querySelector(SCROLL_CONTAINER);
    if (!container) return;
    const fadeTop = document.querySelector(FADE_TOP);
    const fadeBottom = document.querySelector(FADE_BOTTOM);
    const { scrollTop, scrollHeight, clientHeight } = container;
    // 1px tolerance — Safari can leave scrollTop at ~0.5 after a
    // programmatic reset; treating that as "at the top" prevents
    // the top fade from flashing on briefly.
    const canScrollUp = scrollTop > 1;
    const canScrollDown = scrollTop + clientHeight < scrollHeight - 1;
    fadeTop?.classList.toggle(VISIBLE_CLASS, canScrollUp);
    fadeBottom?.classList.toggle(VISIBLE_CLASS, canScrollDown);
  }

  const scrollContainer = document.querySelector(SCROLL_CONTAINER);
  if (scrollContainer) {
    scrollContainer.addEventListener('scroll', updateFilterScrollFades, { passive: true });
    window.addEventListener('resize', updateFilterScrollFades);
  }

  // Open and close filter popup
  $(document).on('click', '.js-filters-popup__open', function (e) {
    e.preventDefault();
    e.stopPropagation();

    $('body').addClass('filters-popup--show');

    // Open all accordion contents by default — merchant preference,
    // so the merchant sees every filter group without having to
    // click through each header. Fires on every popup open so the
    // reveal is authoritative regardless of what the user did with
    // the accordions during a previous session (a header they'd
    // manually collapsed still opens up again on next open).
    // jQuery's slideDown skips already-visible elements, so this
    // is idempotent when re-opening a popup with everything
    // already expanded.
    $('.collection-filter__accordion-content').addClass('is-active').slideDown(slideDuration);
    $('.collection-filter__accordion-header').addClass('is-active');

    // Re-check scroll fades after the open transition has had a
    // frame to settle layout. `clientHeight` reads 0 while the
    // panel is still hidden, so an rAF-then-timeout pair catches
    // both fast (rAF) and slow (post-transition) layout paths.
    requestAnimationFrame(updateFilterScrollFades);
    setTimeout(updateFilterScrollFades, 350);
  });

  $(document).on('click', '.js-filters-popup__close', function (e) {
    e.preventDefault();
    $('body').removeClass('filters-popup--show');
  });

  $(document).on(
    'click',
    '.filters-popup__overlay, .collection-filter__overlay',
    function () {
      $('body').removeClass('filters-popup--show');
    }
  );

  $(document).on('click', '.filters-popup, .collection-filter', function (e) {
    e.stopPropagation();
  });

  // Sync checkboxes between both forms (tab form + popup form)
  $(document).on(
    'change',
    'input[name="filter.p.m.custom.filter_product_type"]',
    function () {
      const value = $(this).val();
      const isChecked = $(this).is(':checked');

      $(`input[name="filter.p.m.custom.filter_product_type"][value="${value}"]`)
        .not(this)
        .prop('checked', isChecked);

      // "All" filter exclusivity logic
      if (value === 'all' && isChecked) {
        $('input[name="filter.p.m.custom.filter_product_type"]')
          .not('[value="all"]')
          .prop('checked', false);
      } else if (value !== 'all' && isChecked) {
        $('input[name="filter.p.m.custom.filter_product_type"][value="all"]').prop('checked', false);
      }
    }
  );

  $(document).on(
    'change',
    'input[value="all"][name="filter.p.m.custom.filter_product_type"]',
    function () {
      if ($(this).is(':checked')) {
        $('input[name="filter.p.m.custom.filter_product_type"]')
          .not(this)
          .prop('checked', false)
          .trigger('change');
      }
    }
  );

  // Accordion functionality — click toggles only the clicked
  // header's own content. Sibling accordions are left alone so the
  // merchant can keep multiple open at once. slideUp / slideDown
  // fire updateFilterScrollFades on animation completion so the
  // top / bottom gradients stay in sync with the container's new
  // scrollHeight after the row expands or collapses.
  $(document).on('click', '.collection-filter__accordion-header', function (e) {
    e.preventDefault();
    const $accordion = $(this).closest('.collection-filter__accordion');
    const $content = $accordion.find('.collection-filter__accordion-content');
    const isActive = $content.hasClass('is-active');

    if (isActive) {
      $content.removeClass('is-active').slideUp(slideDuration, updateFilterScrollFades);
      $(this).removeClass('is-active');
    } else {
      $content.addClass('is-active').slideDown(slideDuration, updateFilterScrollFades);
      $(this).addClass('is-active');
    }
  });

  /*
   * Flag the next renderFilters call as a clear-all so the wrapper
   * below can distinguish it from a plain filter change. Needed
   * because `FacetFiltersForm.onActiveFilterClick` calls
   * `renderPage(url)` without passing the click event through, so
   * the event argument reaches `renderFilters` as `undefined` —
   * indistinguishable from init / cached-refresh paths.
   *
   * Listener must run in the CAPTURE phase. `FacetRemove.closeFilter`
   * is a target-phase listener on the anchor that synchronously
   * calls `renderPage` → `renderSectionFromCache` → `renderFilters`
   * whenever the URL is already cached (i.e. any subsequent Clear
   * All on the same page). A bubble-phase document listener fires
   * AFTER that target listener, which means the flag would be set
   * too late for the cached path — the render would see flag=false
   * and collapse the accordions, then the flag would flip true and
   * incorrectly re-open the NEXT filter change. Capture phase sets
   * the flag before any target-phase click work runs, so both the
   * async fetch path and the sync cache path see the flag correctly
   * at render time.
   *
   * The flag is auto-cleared via setTimeout, NOT by consuming it
   * inside the wrapper below. `renderPage` iterates every registered
   * section (product-grid + collection-filter-tabs on the collection
   * page), so Clear All fires `renderFilters` twice — one per
   * section. If the wrapper consumed the flag on the first call,
   * the second call would swap in un-injected markup and wipe the
   * accordions the first call just opened. Auto-clear on a timer
   * lets every render in the same clear cycle see flag=true.
   */
  let clearAllPending = false;
  let clearAllResetTimer = null;
  document.addEventListener('click', (event) => {
    const trigger = event.target?.closest?.('facet-remove a, .filters-popup__clear');
    if (!trigger) return;
    clearAllPending = true;
    if (clearAllResetTimer) clearTimeout(clearAllResetTimer);
    clearAllResetTimer = setTimeout(() => {
      clearAllPending = false;
      clearAllResetTimer = null;
    }, 2000);
  }, true);

  /*
   * Preserve accordion open state across filter-change re-renders,
   * and reset it on clear-all.
   *
   * FacetFiltersForm.renderFilters swaps innerHTML on every non-
   * matching .js-filter (which IS the whole .collection-filter__
   * accordion element for default-presentation filters). That swap
   * wipes any .is-active class on nested header / content plus the
   * inline display: block jQuery's slideDown left behind, so
   * accordions the user had expanded appear to snap shut on any
   * filter option change. This wrapper snapshots the open indexes
   * before the swap and re-applies them after, so the user's
   * accordion state survives filter interactions.
   *
   * For the Clear All flow (event fires from the clear link, which
   * sits outside any .js-filter), the wrapper skips the snapshot
   * and instead force-expands every accordion after the render —
   * clearing returns the popup to its default resting state, which
   * for this project is "all accordions open" (matches the popup-
   * open handler above).
   *
   * Init / uninstrumented paths (no event) leave state alone.
   */
  if (window.FacetFiltersForm && typeof FacetFiltersForm.renderFilters === 'function') {
    const originalRenderFilters = FacetFiltersForm.renderFilters;
    FacetFiltersForm.renderFilters = function (html, event) {
      const target = event?.target;
      const isFilterChange = !!target?.closest?.('.js-filter');
      // Clear-all detection: prefer the click-time flag (see the
      // handler above — set the moment the clear link is tapped)
      // since onActiveFilterClick drops the event on its way to
      // renderPage. Fall back to event-based detection for any
      // callers that DO propagate a non-filter event. Flag is NOT
      // consumed here — the timer-based reset above handles that,
      // so multiple renderFilters calls in the same clear cycle
      // (one per registered section) all see the flag as true.
      const isClearAction = clearAllPending || (!!event && !isFilterChange);

      const openIndexes = new Set();
      if (isFilterChange) {
        document.querySelectorAll('.filters-popup .js-filter').forEach((el) => {
          const content = el.querySelector('.collection-filter__accordion-content');
          if (content?.classList.contains('is-active')) {
            openIndexes.add(el.dataset.index);
          }
        });
      }

      // Pre-inject `.is-active` into the fresh markup before the
      // original renderer parses + swaps it in. The alternative —
      // adding the class AFTER the swap — leaves the accordions
      // visible in their fresh (no `.is-active`, so `+` icon,
      // `display: none`) state for one paint frame, which reads
      // to the merchant as the accordions blinking closed before
      // reopening. Rewriting the HTML string keeps the swap-in DOM
      // already in the "all open" state, so first paint is
      // correct. Regex-based to catch every accordion regardless
      // of filter presentation (default / grid / swatch / …) as
      // long as it renders these BEM class names.
      let renderHtml = html;
      if (isClearAction && typeof renderHtml === 'string') {
        renderHtml = renderHtml
          .replace(
            /class="collection-filter__accordion-header"/g,
            'class="collection-filter__accordion-header is-active"'
          )
          .replace(
            /class="collection-filter__accordion-content"/g,
            'class="collection-filter__accordion-content is-active"'
          );
      }

      originalRenderFilters.call(this, renderHtml, event);

      if (isFilterChange) {
        openIndexes.forEach((index) => {
          const el = document.querySelector(`.filters-popup .js-filter[data-index="${index}"]`);
          if (!el) return;
          el.querySelector('.collection-filter__accordion-header')?.classList.add('is-active');
          const content = el.querySelector('.collection-filter__accordion-content');
          if (content) {
            content.classList.add('is-active');
            content.style.display = 'block';
          }
        });
      }
      // Note: no post-swap fixup for clear — the pre-injected
      // `.is-active` classes make the CSS rule
      // `.collection-filter__accordion-content.is-active { display: block }`
      // fire on first paint, and the header keeps the same class
      // for the icon-swap chevron rule.

      requestAnimationFrame(updateFilterScrollFades);
    };
  }
});

function onKeyUpEscapeSA(event) {
  if (event.code && event.code.toUpperCase() === 'ESCAPE') {
    const open = event.target.closest('details[open]');
    if (!open) return;
    open.removeAttribute('open');
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounceFacets((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscapeSA);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    document.body.classList.add('sa-facets--loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const productGridContainer = document.getElementById('product-grid-container');
    const parsedProductGrid = parsedHTML.getElementById('product-grid-container');
    if (productGridContainer && parsedProductGrid) {
      productGridContainer.innerHTML = parsedProductGrid.innerHTML;

      /* Re-init Ajaxinate's Load More on the freshly-replaced
       * #product-grid. The original instance bound in
       * snippets/ajaxinate.liquid was tied to the old DOM element,
       * which is now gone — without this re-init, the next
       * Load-More click falls back to native <a> navigation and
       * the page reloads, losing the AJAX state.
       *
       * Destroys any prior endlessScroll instance first so we
       * don't accumulate listeners across successive filter / sort
       * changes. */
      if (typeof Ajaxinate !== 'undefined' && document.querySelector('#product-grid') && document.querySelector('#AjaxinatePagination')) {
        if (window.endlessScroll) {
          try { window.endlessScroll.destroy(); } catch (e) {}
        }
        window.endlessScroll = new Ajaxinate({
          container: '#product-grid',
          method: 'click',
          callback: function (data) {
            document.body.classList.remove('ajaxinate--loading');
            if (typeof ajaxinateCallback === 'function') ajaxinateCallback();
            try {
              const params = new URL(data.URL).searchParams;
              const page = params.get('page');
              if (page) {
                const newUrl = new URL(window.location.href);
                newUrl.hash = 'page-' + page;
                history.replaceState(history.state, '', newUrl.toString());
              }
            } catch (e) {}
          },
        });
      }
    }

    const collectionFilterTabs = document.querySelector('.collection-filter-tabs');
    const parsedFilterTabs = parsedHTML.querySelector('.collection-filter-tabs');
    if (collectionFilterTabs && parsedFilterTabs) {
      collectionFilterTabs.innerHTML = parsedFilterTabs.innerHTML;
    }

    document.body.classList.remove('sa-facets--loading');

    if (window.indentGridProducts) {
      indentGridProducts();
    }
  }

  static renderProductCount(html) {
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedDoc?.getElementById('ProductCount');
    const count = sourceCount ? sourceCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) container.innerHTML = count;

    $('.js-filters-count').html(count);

    document.body.classList.remove('sa-facets--loading');

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      document.body.classList.remove('sa-facets--loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #CollectionFilterTabsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);

      if (targetElement) {
        targetElement.innerHTML = element.innerHTML;
        targetElement.classList.remove('active');
      }

      window?.initFiltersSwiper?.();
      const filtersCount = $('.filters-popup input:checked').length;

      $('.js-filters-applied').text(filtersCount > 0 ? ` • ${filtersCount}` : '');
      $('.applied-count, .collection-filter-tabs__filters-btn--remove').toggleClass(
        'active',
        filtersCount > 0
      );
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceEl = html.querySelector(selector);
      if (!sourceEl) return;
      const targetEl = document.querySelector(selector);
      if (targetEl) targetEl.innerHTML = sourceEl.innerHTML;
    });

    // H3.0 has no <menu-drawer> custom element; guard the legacy call.
    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const sections = [];
    const grid = document.getElementById('product-grid');
    if (grid) sections.push({ section: grid.dataset.id });
    const tabs = document.querySelector('.collection-filter-tabs');
    if (tabs && tabs.dataset.sectionId) sections.push({ section: tabs.dataset.sectionId });
    return sections;
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (let [key, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        params.append(key, value);
      }
    }

    return params.toString();
  }

  createCombinedSearchParams(forms) {
    const allParams = new URLSearchParams();
    const processedKeys = new Set();

    forms.forEach((form) => {
      const formData = new FormData(form);

      for (let [key, value] of formData.entries()) {
        if (!value || value.trim() === '') continue;

        if (key === 'filter.p.m.custom.filter_product_type') {
          if (!processedKeys.has(`${key}:${value}`)) {
            allParams.append(key, value);
            processedKeys.add(`${key}:${value}`);
          }
        } else {
          allParams.append(key, value);
        }
      }
    });

    return allParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const triggeringForm = event.target.closest('form');

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(triggeringForm);
      this.onSubmitForm(searchParams, event);
      return;
    }

    if (triggeringForm.id === 'CollectionFilterTabsForm' || triggeringForm.id === 'FacetFiltersForm') {
      const searchParams = this.createSearchParams(triggeringForm);
      this.onSubmitForm(searchParams, event);
      return;
    }

    const forms = [];
    const isMobile = triggeringForm.id === 'FacetFiltersFormMobile';
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetSortDrawerForm') {
          const noJsElements = document.querySelectorAll('.no-js-list');
          noJsElements.forEach((el) => el.remove());
          forms.push(form);
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(form);
      }
    });

    const searchParams = this.createCombinedSearchParams(forms);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    // Let through OS shortcuts (paste/copy/cut/select-all). v3.0 only
    // checked metaKey, which blocks Ctrl+V on Windows.
    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') form.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}
